import { motion, AnimatePresence, useInView } from 'framer-motion';
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FolderKanban, Package, BarChart3, MessageSquare, Users, FileSpreadsheet, Calculator, Blocks, Shield, ArrowRight } from 'lucide-react';
import {
  ProjectIllustration,
  SalesIllustration,
  FinanceIllustration,
  SecureIllustration,
  CustomIllustration,
} from './ServiceIllustrations';

const primaryServices = [
  {
    icon: FolderKanban,
    title: 'Project & Team Systems',
    tagline: 'Replace Trello, Asana, or Monday with something built for your exact process.',
    prefill: 'Project Management System - Task tracking, team collaboration, and project planning tool',
    Illustration: ProjectIllustration,
  },
  {
    icon: BarChart3,
    title: 'Sales & CRM Dashboards',
    tagline: 'Track leads, manage pipelines, and see real-time performance in one place.',
    prefill: 'CRM & Sales Dashboard - Lead tracking, pipeline management, and sales analytics',
    Illustration: SalesIllustration,
  },
  {
    icon: Calculator,
    title: 'Finance & Reporting',
    tagline: 'Invoicing, expense tracking, and financial reports built around your numbers.',
    prefill: 'Accounting Software - Custom invoicing, expense tracking, and financial reporting',
    Illustration: FinanceIllustration,
  },
  {
    icon: Shield,
    title: 'Secure Internal Tools',
    tagline: 'Encrypted databases, access control, and audit trails for sensitive data.',
    prefill: 'Encrypted Database/Vault - Secure storage with role-based access control',
    Illustration: SecureIllustration,
  },
  {
    icon: Blocks,
    title: 'Custom Software',
    tagline: 'If you can describe the problem, we can build the system for it.',
    prefill: 'Custom Software Solution - Bespoke system for unique business requirements',
    Illustration: CustomIllustration,
  },
];

const moreServices = [
  { icon: Package, title: 'Inventory & Stock', prefill: 'Inventory Management System - Real-time stock tracking with automated reordering' },
  { icon: FileSpreadsheet, title: 'Reporting & Analytics', prefill: 'Reporting & Analytics Dashboard - Custom data visualization and automated reporting' },
  { icon: Users, title: 'HR & Employee Systems', prefill: 'HR System - Employee records, leave management, and performance tracking' },
  { icon: MessageSquare, title: 'Internal Communication', prefill: 'Internal Communication Tool - Secure team messaging and collaboration' },
];

export const ServicesSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [active, setActive] = useState(0);
  const current = primaryServices[active];
  const Illustration = current.Illustration;
  const quoteUrl = `/instant-quote?project=${encodeURIComponent(current.prefill)}`;

  return (
    <section id="services" className="py-24 md:py-32 bg-[#F8FAFC]" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8 }}
          className="mb-16 md:mb-20"
        >
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-4">
            What We Build
          </p>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-[#003333] max-w-4xl mb-6">
            Internal Systems That Drive Growth
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl">
            Replace spreadsheets, fragile workflows, and disconnected tools with custom software built around how your business actually works.
          </p>
        </motion.div>

        {/* Tabs + illustration */}
        <div className="grid md:grid-cols-5 gap-8 md:gap-12 items-start mb-10">
          {/* Tab list */}
          <div className="md:col-span-2 flex flex-row md:flex-col gap-2 overflow-x-auto md:overflow-visible pb-2 md:pb-0">
            {primaryServices.map((service, i) => {
              const Icon = service.icon;
              const isActive = i === active;
              return (
                <button
                  key={service.title}
                  onClick={() => setActive(i)}
                  className={`shrink-0 md:shrink text-left flex items-start gap-3 rounded-xl px-4 py-3.5 border transition-all duration-300 ${
                    isActive
                      ? 'bg-white border-[#003333]/20 shadow-md'
                      : 'bg-transparent border-transparent hover:bg-white/60'
                  }`}
                  data-testid={`service-tab-${i}`}
                >
                  <div
                    className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 transition-all duration-300 ${
                      isActive ? 'bg-[#003333] text-white' : 'bg-white text-[#003333]/50'
                    }`}
                  >
                    <Icon className="w-[18px] h-[18px]" />
                  </div>
                  <div className="min-w-[160px] md:min-w-0">
                    <p className={`text-sm font-semibold mb-0.5 whitespace-nowrap md:whitespace-normal ${isActive ? 'text-[#003333]' : 'text-[#003333]/70'}`}>
                      {service.title}
                    </p>
                    <p
                      className={`text-xs text-slate-500 leading-relaxed hidden md:block transition-opacity duration-300 ${
                        isActive ? 'opacity-100' : 'opacity-0'
                      }`}
                    >
                      {service.tagline}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Illustration panel */}
          <div className="md:col-span-3">
            <AnimatePresence mode="wait">
              <motion.div
                key={current.title}
                initial={{ opacity: 0, x: 16 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -16 }}
                transition={{ duration: 0.4 }}
              >
                <Illustration />
              </motion.div>
            </AnimatePresence>
            <Link
              to={quoteUrl}
              className="mt-5 inline-flex items-center gap-2 text-[#003333] font-medium text-sm hover:gap-4 transition-all hover:text-[#004d4d]"
              data-testid="service-quote-link"
            >
              <span>Get a quote for {current.title.toLowerCase()}</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        {/* Everything else - compact row */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="flex flex-wrap items-center gap-3"
        >
          <span className="text-sm text-slate-400 mr-1">Also build:</span>
          {moreServices.map((service) => {
            const Icon = service.icon;
            const url = `/instant-quote?project=${encodeURIComponent(service.prefill)}`;
            return (
              <Link
                key={service.title}
                to={url}
                className="inline-flex items-center gap-2 bg-white border border-slate-200 hover:border-[#003333]/30 rounded-full px-4 py-2 text-sm text-[#003333]/80 hover:text-[#003333] transition-colors"
              >
                <Icon className="w-4 h-4" />
                {service.title}
              </Link>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};
